"use client";

import React, { createContext, useContext, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import { WalletType } from "@generated/types";

/**
 * The two details-step forms, owned ABOVE the wizard's step-keyed animation.
 *
 * The wizard swaps steps inside an AnimationWrapper keyed on the step with
 * mode="wait", so every step unmounts on every step change. A form owned by a
 * step dies with it, which is how Back used to drop what the user typed. These
 * two live here instead, so Back and Continue never drop or replace a value;
 * the steps read them through useFormContext / useWatch / useFormState.
 *
 * Render rule: nothing at this level calls watch() or reads formState. Either
 * would subscribe this host — and through it the layout's sticky progress bar
 * — to every keystroke. Read formState in the steps with useFormState, never
 * from useFormContext().formState (that is the host's proxy).
 *
 * The forms keep fields that are no longer rendered (shouldUnregister:false),
 * so `handleSubmit` data can carry values the user can no longer see. The
 * payload builders (buildCreateRequests.ts) are what strip them.
 */

export type FiatDetailsFormValues = {
  nickname: string;
  reference: string;
  address: {
    firstName: string;
    lastName: string;
    corporationName: string;
    line1: string;
    line2: string;
    postTown: string;
    state: string;
    postCode: string;
    country: string;
  };
};

export type BlockchainDetailsFormValues = {
  address: string;
  nickname: string;
  vaspId: string;
  vaspName: string;
  vaspAdditionalInfo: string;
  walletType: WalletType;
};

/**
 * What a restart resets the fiat form to. Every key is spelled out: a bare
 * `reset()` or `reset({})` restores react-hook-form's stored default values,
 * which a seeded reset (e.g. a story) may have replaced.
 */
export const EMPTY_FIAT_DETAILS: FiatDetailsFormValues = {
  nickname: "",
  reference: "",
  address: {
    firstName: "",
    lastName: "",
    corporationName: "",
    line1: "",
    line2: "",
    postTown: "",
    state: "",
    postCode: "",
    country: "",
  },
};

/** The blockchain twin of EMPTY_FIAT_DETAILS; a restart lands on the VASP branch. */
export const EMPTY_BLOCKCHAIN_DETAILS: BlockchainDetailsFormValues = {
  address: "",
  nickname: "",
  vaspId: "",
  vaspName: "",
  vaspAdditionalInfo: "",
  walletType: WalletType.Vasp,
};

type RecipientForms = {
  fiatDetailsForm: UseFormReturn<FiatDetailsFormValues>;
  blockchainDetailsForm: UseFormReturn<BlockchainDetailsFormValues>;
};

const RecipientFormsContext = createContext<RecipientForms | undefined>(
  undefined
);

export function RecipientFormsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const fiatDetailsForm = useForm<FiatDetailsFormValues>({
    defaultValues: EMPTY_FIAT_DETAILS,
  });
  const blockchainDetailsForm = useForm<BlockchainDetailsFormValues>({
    defaultValues: EMPTY_BLOCKCHAIN_DETAILS,
  });

  // useForm returns the same object for the life of the mount, so this value
  // never changes and consumers never re-render through it.
  const value = useMemo(
    () => ({ fiatDetailsForm, blockchainDetailsForm }),
    [fiatDetailsForm, blockchainDetailsForm]
  );

  return (
    <RecipientFormsContext.Provider value={value}>
      {children}
    </RecipientFormsContext.Provider>
  );
}

export function useRecipientForms(): RecipientForms {
  const context = useContext(RecipientFormsContext);
  if (context === undefined) {
    throw new Error(
      "useRecipientForms must be used within a RecipientFormsProvider"
    );
  }
  return context;
}
